import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

interface SelectProps {
  id?: string;
  name?: string;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  containerClassName?: string;
  onChange?: (event: { target: { value: string } }) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

interface OptionItem {
  value: string;
  label: string;
  /** Dòng phụ dưới nhãn trong danh sách (ví dụ tên tiếng Hoa), từ `data-sublabel` của <option>. */
  sublabel?: string;
  disabled?: boolean;
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) {
    return textOf((node.props as { children?: ReactNode }).children);
  }
  return "";
}

function flattenOptions(children: ReactNode): OptionItem[] {
  const items: OptionItem[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === "optgroup") {
      items.push(...flattenOptions((child.props as { children?: ReactNode }).children));
      return;
    }
    if (child.type !== "option") return;
    const props = child.props as {
      value?: string | number;
      children?: ReactNode;
      disabled?: boolean;
      "data-sublabel"?: string;
    };
    items.push({
      value: props.value != null ? String(props.value) : "",
      label: textOf(props.children),
      sublabel: props["data-sublabel"] || undefined,
      disabled: props.disabled,
    });
  });
  return items;
}

/** Bề rộng tối đa của danh sách; chữ dài hơn thì xuống dòng. */
const MENU_MAX_WIDTH = 360;

/**
 * Custom listbox. Native <select> paints the OS menu (dark macOS chrome) and
 * cannot follow design tokens — this portal menu uses brand/surface/line.
 * Callers keep the same <option> children + onChange(e.target.value) contract.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      className,
      containerClassName,
      children,
      value,
      defaultValue,
      disabled,
      id,
      name,
      onChange,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
    },
    ref,
  ) => {
    const listId = useId();
    const options = useMemo(() => flattenOptions(children), [children]);
    const valueStr = value != null ? String(value) : defaultValue != null ? String(defaultValue) : "";
    const selected = options.find((o) => o.value === valueStr);
    const triggerLabel = selected?.label || valueStr || "\u00a0";

    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

    const setTriggerRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    const close = () => {
      setOpen(false);
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    };

    const emit = (next: string) => {
      onChange?.({ target: { value: next } });
    };

    const select = (next: string) => {
      emit(next);
      close();
    };

    const enabledIndexes = () =>
      options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);

    const move = (delta: number) => {
      const enabled = enabledIndexes();
      if (enabled.length === 0) return;
      setActive((current) => {
        const pos = enabled.indexOf(current);
        const from = pos === -1 ? 0 : pos;
        return enabled[(from + delta + enabled.length) % enabled.length] ?? enabled[0]!;
      });
    };

    useLayoutEffect(() => {
      if (!open) {
        setMenuPos(null);
        return;
      }
      const place = () => {
        const box = triggerRef.current?.getBoundingClientRect();
        if (!box) return;
        // Danh sách ít nhất bằng ô, được nở theo chữ tới MENU_MAX_WIDTH rồi xuống dòng — không cắt chữ
        // (Roary 19/10/2026: "Metal Buttons · 金属钮扣" bị cắt mất phần tiếng Hoa). Đo bề rộng thật để không tràn mép phải.
        const width = Math.max(box.width, 176);
        const menuW = menuRef.current?.offsetWidth ?? width;
        const left = Math.min(Math.max(8, box.left), window.innerWidth - menuW - 8);
        const menuH = menuRef.current?.offsetHeight ?? 256;
        const below = box.bottom + 4;
        const top =
          below + menuH > window.innerHeight - 8 && box.top > menuH + 8
            ? box.top - menuH - 4
            : below;
        setMenuPos({ top, left, width });
      };
      const selectedIdx = Math.max(0, options.findIndex((o) => o.value === valueStr && !o.disabled));
      setActive(selectedIdx);
      place();
      window.requestAnimationFrame(place);
      window.addEventListener("resize", place);
      window.addEventListener("scroll", place, true);
      return () => {
        window.removeEventListener("resize", place);
        window.removeEventListener("scroll", place, true);
      };
    }, [open, options, valueStr]);

    useEffect(() => {
      if (!open) return;
      const onPointerDown = (event: PointerEvent) => {
        const target = event.target as Node;
        if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
        setOpen(false);
      };
      document.addEventListener("pointerdown", onPointerDown);
      return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [open]);

    useEffect(() => {
      optionRefs.current[active]?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!open) setOpen(true);
        else move(event.key === "ArrowDown" ? 1 : -1);
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        const next = options[active];
        if (next && !next.disabled) select(next.value);
        return;
      }
      if (event.key === "Escape" && open) {
        event.preventDefault();
        close();
      }
    };

    return (
      <div ref={rootRef} className={cn("relative flex h-9 items-center", containerClassName)}>
        {name ? (
          <input type="hidden" name={name} value={valueStr} disabled={disabled} />
        ) : null}
        <button
          ref={setTriggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => {
            if (disabled) return;
            if (open) close(); else setOpen(true);
          }}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "flex h-full w-full items-center gap-2 rounded-sm border border-line-strong bg-surface py-0 pl-2.5 pr-2.5 text-left text-sm text-ink",
            "transition-colors hover:border-ink-faint",
            "focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-brand)]/15",
            "disabled:pointer-events-none disabled:opacity-45",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate" title={triggerLabel || undefined}>{triggerLabel}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn("size-4 shrink-0 text-ink-faint transition-transform", open && "rotate-180")}
          />
        </button>

        {open && menuPos
          ? createPortal(
              <div
                ref={menuRef}
                className="fixed z-[120] overflow-hidden rounded-md border border-line bg-surface shadow-[var(--shadow-pop)]"
                style={{
                  top: menuPos.top,
                  left: menuPos.left,
                  minWidth: menuPos.width,
                  width: "max-content",
                  maxWidth: `min(${MENU_MAX_WIDTH}px, calc(100vw - 16px))`,
                }}
              >
                <div id={listId} role="listbox" className="max-h-64 overflow-y-auto p-1">
                  {options.length === 0 ? (
                    <p className="px-2.5 py-3 text-center text-xs text-ink-faint">—</p>
                  ) : (
                    options.map((option, index) => {
                      const isSelected = option.value === valueStr;
                      const isActive = index === active;
                      return (
                        <button
                          key={`${option.value}:${index}`}
                          ref={(node) => {
                            optionRefs.current[index] = node;
                          }}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          disabled={option.disabled}
                          onMouseEnter={() => {
                            if (!option.disabled) setActive(index);
                          }}
                          onClick={() => {
                            if (!option.disabled) select(option.value);
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors",
                            option.disabled && "cursor-not-allowed opacity-45",
                            isSelected ? "bg-brand-tint text-brand-ink" : "text-ink-soft",
                            isActive && !isSelected && !option.disabled && "bg-sunken text-ink",
                          )}
                        >
                          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                            {option.label || "\u00a0"}
                            {option.sublabel ? (
                              <span className="block text-2xs text-ink-faint">{option.sublabel}</span>
                            ) : null}
                          </span>
                          {isSelected ? (
                            <Check aria-hidden="true" className="size-4 shrink-0 text-brand" />
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  },
);
Select.displayName = "Select";
